import React, { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api, formatApiErrorDetail } from "../lib/api";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Label } from "./ui/label";
import { Megaphone, Trash2, Plus } from "lucide-react";
import { toast } from "sonner";
import { fmtDate } from "../lib/format";
import { Confirm } from "./Shared";

export function AnnouncementsManager() {
  const qc = useQueryClient();
  const { data: items = [] } = useQuery({ queryKey: ["announcements"], queryFn: () => api.get("/announcements").then((r) => r.data) });
  const [title, setTitle] = useState("");
  const [message, setMessage] = useState("");
  const [saving, setSaving] = useState(false);

  const post = async () => {
    if (!title.trim() || !message.trim()) { toast.error("Title and message are required."); return; }
    setSaving(true);
    try {
      await api.post("/announcements", { title, message });
      setTitle(""); setMessage("");
      qc.invalidateQueries({ queryKey: ["announcements"] });
      toast.success("Announcement posted");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };
  const remove = async (id) => {
    try { await api.delete(`/announcements/${id}`); qc.invalidateQueries({ queryKey: ["announcements"] }); toast.success("Removed"); }
    catch { toast.error("Could not remove."); }
  };

  return (
    <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-4 sm:p-6 mt-6" data-testid="announcements-card">
      <div className="flex items-center gap-2 mb-1"><Megaphone className="h-5 w-5 text-slate-500" /><div className="font-heading font-semibold text-slate-800">Announcements</div></div>
      <p className="text-sm text-slate-500 mb-5">Posted messages appear on the dashboard for Employees and Interns.</p>
      <div className="grid sm:grid-cols-2 gap-4 items-start">
        <div className="space-y-2">
          <Label className="text-sm">Title</Label>
          <Input data-testid="ann-title" value={title} onChange={(e) => setTitle(e.target.value)} placeholder="e.g. Office closed Friday" />
          <Label className="text-sm">Message</Label>
          <Textarea data-testid="ann-message" value={message} onChange={(e) => setMessage(e.target.value)} rows={3} placeholder="Short details…" />
          <Button data-testid="ann-post" onClick={post} disabled={saving} className="bg-[#1E3A8A] hover:bg-[#1D4ED8]"><Plus className="h-4 w-4 mr-2" />{saving ? "Posting…" : "Post announcement"}</Button>
        </div>
        <div className="space-y-2">
          {items.length === 0 ? <p className="text-sm text-slate-400">No announcements yet.</p> : items.map((a) => (
            <div key={a.id} data-testid={`ann-item-${a.id}`} className="rounded-lg border border-slate-200 p-3">
              <div className="flex items-start justify-between gap-2">
                <div className="font-medium text-slate-800 text-sm">{a.title}</div>
                <Confirm onConfirm={() => remove(a.id)} title="Delete announcement?" trigger={<Button size="icon" variant="ghost" data-testid={`ann-delete-${a.id}`} className="text-red-600 h-7 w-7"><Trash2 className="h-4 w-4" /></Button>} />
              </div>
              <div className="text-sm text-slate-600 mt-1 whitespace-pre-wrap">{a.message}</div>
              <div className="text-xs text-slate-400 mt-1">{fmtDate(a.created_at)}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
